import { useRef } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { Bold, Heading1, Heading2, Heading3, Italic, Link2, List } from 'lucide-react';
import Markdown from '@/components/Markdown';

interface MarkdownTextareaProps {
  registration: UseFormRegisterReturn;
  /** Current field value (from `watch`), used for the preview. */
  value: string | undefined;
  /** Writes the edited text back into the form (from `setValue`). */
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

type Format = 'bold' | 'italic' | 'link' | 'list' | 'h1' | 'h2' | 'h3';

const HEADING_PREFIX = /^#{1,6} /;

/**
 * Textarea with a few formatting shortcuts and a live preview underneath.
 * Text is stored as markdown and rendered with the shared Markdown component.
 */
export function MarkdownTextarea({ registration, value, onChange, placeholder, className = '' }: MarkdownTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const applyFormat = (format: Format) => {
    const el = textareaRef.current;
    if (!el) return;
    const text = el.value;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = text.slice(start, end);

    let insert: string;
    let selStart: number;
    let selEnd: number;
    if (format === 'list' || format.startsWith('h')) {
      // Line-level formats apply to every line the selection touches.
      const lineStart = text.lastIndexOf('\n', start - 1) + 1;
      const block = text.slice(lineStart, end);
      const formatLine = (line: string) => {
        if (format === 'list') return line.startsWith('- ') ? line : `- ${line}`;
        // Headings replace any existing heading level; same level again toggles it off.
        const prefix = `${'#'.repeat(Number(format[1]))} `;
        const bare = line.replace(HEADING_PREFIX, '');
        return line.startsWith(prefix) ? bare : prefix + bare;
      };
      const formatted = block.split('\n').map(formatLine).join('\n');
      onChange(text.slice(0, lineStart) + formatted + text.slice(end));
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(lineStart + formatted.length, lineStart + formatted.length);
      });
      return;
    }
    if (format === 'link') {
      const label = selected || 'link text';
      insert = `[${label}](https://)`;
      // Select the URL placeholder so it can be typed over.
      selStart = start + label.length + 3;
      selEnd = selStart + 'https://'.length;
    } else {
      const marker = format === 'bold' ? '**' : '_';
      const inner = selected || (format === 'bold' ? 'bold text' : 'italic text');
      insert = `${marker}${inner}${marker}`;
      selStart = start + marker.length;
      selEnd = selStart + inner.length;
    }
    onChange(text.slice(0, start) + insert + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const toolButtons: { format: Format; icon: typeof Bold; title: string }[] = [
    { format: 'h1', icon: Heading1, title: 'Heading 1' },
    { format: 'h2', icon: Heading2, title: 'Heading 2' },
    { format: 'h3', icon: Heading3, title: 'Heading 3' },
    { format: 'bold', icon: Bold, title: 'Bold' },
    { format: 'italic', icon: Italic, title: 'Italic' },
    { format: 'link', icon: Link2, title: 'Link' },
    { format: 'list', icon: List, title: 'Bulleted list' },
  ];

  return (
    <div>
      <div className="flex justify-end gap-0.5 mb-1.5">
        {toolButtons.map(({ format, icon: Icon, title }) => (
          <button
            key={format}
            type="button"
            title={title}
            aria-label={title}
            onClick={() => applyFormat(format)}
            className="p-1.5 rounded-md text-dark-muted hover:text-dark-text hover:bg-dark-border transition-colors"
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>

      <textarea
        {...registration}
        ref={el => {
          registration.ref(el);
          textareaRef.current = el;
        }}
        className={`input min-h-[100px] ${className}`}
        placeholder={placeholder}
      />
      <p className="text-xs text-dark-muted mt-1">Markdown supported: # headings, **bold**, _italic_, [links](https://…), lists.</p>

      {value?.trim() && (
        <div className="mt-3">
          <p className="text-xs font-medium text-dark-muted mb-1">Preview</p>
          {/* Not `.input`: DaisyUI gives that a fixed height and nowrap, so content overflows. */}
          <div className="w-full px-4 py-3 rounded-xl border border-dashed border-neutral-400 dark:border-neutral-600 break-words overflow-x-auto">
            <Markdown size="base">{value}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}
